/**
 * 后台取数钩子：三态（加载中 / 失败 / 就绪）统一在这里处理。
 *
 * 不用 react-query：后台的数据都是「打开页面看一次」，没有跨页缓存与失效联动，
 * 引入一层缓存反而把「刷新后仍然是旧数」这件事变得难以解释。
 */
import { useCallback, useEffect, useState } from 'react';

export interface LoadState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

export function useLoad<T>(fetcher: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[] = []): LoadState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // fetcher 通常是每次渲染新建的箭头函数，因此不进依赖数组：进数组会无限循环。
  // 需要重新取数的场景一律显式调用 reload()，语义比「依赖变化自动重取」更可控。
  //
  // `deps` 是给**筛选条件**用的：列表页的状态 / 级次 / 关键字变化必须真的重新取数，
  // 否则界面上的筛选控件点了没反应（而表头还写着当前筛选）—— 那是比报错更坏的结果。
  // 长度在一次挂载内保持稳定，因此展开进依赖数组是安全的。
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    setLoading(true);
    setError(null);

    fetcher(controller.signal)
      .then((result) => {
        if (!alive) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err);
        setLoading(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
    // eslint 不在本仓库的工具链里，这里用注释说明依赖刻意的取舍
  }, [tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { data, error, loading, reload };
}
